"use client";
import React, { useEffect, useState } from "react";
import {
  getFriendlyName,
  renderCloneNode,
  shouldHideFilter,
  showUnmatchedFiltersMessage,
  transformFilterData,
} from "./utils";

export interface FilterValue {
  value: string;
  label?: string;
}

export type FilterValues = string[] | FilterValue[];

export interface FilterOption {
  key: string;
  label: string;
  values: FilterValues;
  hidden?: boolean;
}

export interface FilterData {
  filters: Record<string, string[]>;
}

export type SelectedFilters = Record<string, string>;
const backendUrl = "https://impulse-backend.heliumbuilder.com";

function CollectionFilters({
  filterData,
}: {
  filterData: Record<string, string[] | string>;
}) {
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [loading, setLoading] = useState(false);
  const productArea =
    document.getElementById("product-area") ??
    document.querySelector(
      ".grid.grid-cols-2.sm\\:grid-cols-3.lg\\:grid-cols-4.gap-4",
    ) ??
    document.getElementById("product-grid");
  const pagination = document.querySelector(
    ".pagination-wrapper",
  ) as HTMLElement;
  const productAreaWrapper = document.getElementById(
    "ProductGridContainer",
  ) as HTMLElement;
  const productCount = document.getElementById("ProductCount") as HTMLElement;
  const messageContainer = document.querySelector(
    ".filter-message",
  ) as HTMLElement;
  console.info(productCount, "working");

  function handleFilterChange(updatedFilters: SelectedFilters) {
    setLoading(true);
    if (productArea && loading) {
      productArea.innerHTML = "Loading...";
      pagination.innerHTML = "";
      productCount.innerHTML = "";
      productAreaWrapper.innerHTML = "";
    }
    if (Object.keys(updatedFilters).length === 0) return;

    getProduct(updatedFilters);
  }

  async function getProduct(updatedFilters: SelectedFilters) {
    try {
      const response = await fetch(
        `${backendUrl}/api/filterable-subcollection/filtered-products`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            brandId: window.helium.brandData.storeId,
          },
          body: JSON.stringify({
            filters: {
              ...transformFilterData(updatedFilters),
              subcategory: [filterData.subcategory],
            },
          }),
        },
      );
      const { productIds, unmatchedFilters } = await response.json();
      if (productIds.length === 0) {
        setLoading(false);
        if (productArea) productArea.innerHTML = "No products found";
        pagination.innerHTML = "";
        productCount.innerHTML = "";
        messageContainer.style.display = "none";
        return;
      }
      const productsResponse = await Promise.all(
        productIds.map(async (id: string) => {
          const response = await window.helium.datastore.getProduct({
            id,
          });

          const product = response.details;

          if (!product) {
            return null;
          }

          return product;
        }),
      );
      const products = productsResponse.filter(Boolean);
      const productHtmls = await Promise.all(
        products.map((item) => renderCloneNode(item)),
      );
      const finalHtml = productHtmls.map((html) => html?.__html).join("");

      requestAnimationFrame(() => {
        if (productArea) {
          productArea.innerHTML = finalHtml;
          productCount.innerHTML = `${productHtmls.length} Products`;
        }
        if (productHtmls.length > 0 && unmatchedFilters.length > 0) {
          showUnmatchedFiltersMessage(
            unmatchedFilters,
            productAreaWrapper,
            updatedFilters,
          );
        }
        setLoading(false);
      });
    } catch (e) {
      setLoading(false);
      if (productArea) productArea.innerHTML = "No products found";
      pagination.innerHTML = "";
      messageContainer.style.display = "none";
      console.error(e);
    }
  }

  useEffect(() => {
    if (Object.keys(selectedFilters).length === 0) return;
    handleFilterChange(selectedFilters);
  }, [selectedFilters, filterData]);

  const handleSelectChange = (filterKey: string, value: string) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };
      if (value === "") {
        delete newFilters[filterKey];
      } else {
        newFilters[filterKey] = value;
      }
      return newFilters;
    });
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
    window.location.reload();
  };

  const visibleFilterKeys = Object.keys(filterData?.filters || {}).filter(
    (key) => !shouldHideFilter(key),
  );

  const hasActiveFilters = Object.keys(selectedFilters).length > 0;

  return (
    <div className="hp-py-4 md:hp-py-6 hp-space-y-4 hp-grid">
      <h2 className="hp-font-bold">Let’s Get Specific!</h2>
      <div
        className="swimlane2 hp-pb-0 !hp-gap-4 !hp-items-start"
        style={{
          paddingLeft: "0px",
        }}
      >
        {visibleFilterKeys.map((filterKey) => {
          const values = filterData.filters[filterKey] || [];
          const selectedValue = selectedFilters[filterKey] || "";
          if (filterKey.includes("isMulticolor")) return;
          return (
            <div
              className="hp-flex-shrink-0 hp-mr-2 hp-mb-4 hp-relative"
              key={filterKey}
            >
              <label htmlFor="fabric-select" className="hp-sr-only">
                Fabric
              </label>
              <div className="hp-relative">
                <select
                  id="fabric-select"
                  className={`hp-custom-select hp-block hp-w-full hp-px-6 hp-pr-10 hp-py-4 hp-border hp-border-gray-300 hp-font-semibold hp-text-[14px] hp-leading-[15.6px] hp-tracking-[1.5px] hp-align-middle hp-transition-colors hp-duration-200 hp-ease-in-out hp-hover:border-gray-500 hp-min-w-[150px] appearance-none ${
                    selectedValue
                      ? "hp-bg-[#093174] hp-text-white hp-font-semibold"
                      : "hp-bg-white hp-font-semibold"
                  } `}
                  value={selectedValue}
                  onChange={(e) =>
                    handleSelectChange(filterKey, e.target.value)
                  }
                >
                  <option value="" className="hp-text-gray-300 hp-bg-gray-300">
                    {getFriendlyName(filterKey).replace(/\b\w/g, (c) =>
                      c.toUpperCase(),
                    )}
                  </option>

                  {(values as string[]).map((value) => {
                    const displayValue =
                      value === 0
                        ? "No"
                        : value === 1
                          ? "Yes"
                          : String(value).replace(/\b\w/g, (c) =>
                              c.toUpperCase(),
                            );

                    return (
                      <option key={value} value={value}>
                        {displayValue}
                      </option>
                    );
                  })}
                </select>

                {/* Chevron icon positioned correctly */}
                <div className="hp-pointer-events-none hp-absolute hp-inset-y-0 hp-right-3 hp-flex hp-items-center text-gray-700">
                  <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                    <path d="M7 5l5 5-5 5z" />
                  </svg>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          className="hp-text-[14px] hp-pb-4 hp-text-gray-600 hover:hp-underline"
          onClick={handleClearFilters}
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

export default CollectionFilters;
